/**
 * Design Tokens - DramaDetector Brand Identity
 *
 * Y2K + Glassmorphism + Light Skeuomorphism
 * Based on DESIGN_SYSTEM.md and multi-model consensus (Gemini 2.5 Pro + GPT-5 Pro)
 *
 * CRITICAL RULES:
 * 1. Primary CTAs MUST use solid dark teal (WCAG AAA contrast)
 * 2. Iridescent gradients are DECORATIVE ONLY (borders, backgrounds, focus rings)
 * 3. Glassmorphism requires fallback for web (backdrop-filter polyfill)
 * 4. NEVER animate blur radius (GPU performance on Android)
 *
 * Created: 2025-01-13
 */

/**
 * Brand Colors - Y2K Iridescent Palette
 *
 * Usage:
 * - Primary CTAs: Use BRAND_COLORS.primary (solid dark teal)
 * - Decorative: Use BRAND_COLORS.gradient* for borders/backgrounds
 * - Accessibility: All combinations tested for WCAG AA/AAA compliance
 */
export const BRAND_COLORS = {
  // Primary CTA (WCAG AAA: 12.63:1 contrast on white)
  primary: '#0F766E', // Dark teal
  primaryDark: '#0B3B34', // Even darker for pressed state
  primaryLight: '#14B8A6', // Lighter teal for hover
  primaryText: '#FFFFFF', // White text on dark teal

  // Iridescent Gradient (Y2K Aesthetic - DECORATIVE ONLY)
  gradientPink: '#FFB3E6', // Pink
  gradientLavender: '#D4A5FF', // Lavender
  gradientMint: '#A5FFEB', // Mint
  gradientTeal: '#14B8A6', // Teal

  // Glassmorphism
  glassBackground: 'rgba(255, 255, 255, 0.9)', // 90% white opacity
  glassBorder: 'rgba(255, 255, 255, 0.2)', // 20% white border
  glassBlur: 12, // 12px blur radius (static, never animate)

  // Traffic Light System (Tier-Invariant)
  safe: '#7ED321', // Green - Low drama
  caution: '#F5A623', // Orange - Moderate drama
  danger: '#D00221', // Red - High drama

  // Layer 2 Premium Accents (Drama+ tier)
  accentGold: '#C8A882', // Brushed Gold (12.63:1 contrast on black)
  accentBlush: '#FFF0F0', // Soft Blush

  // Neutrals (Inter font, all tiers)
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
} as const;

/**
 * Iridescent Gradient String
 *
 * Usage:
 * - LinearGradient component (React Native)
 * - CSS background-image (Web)
 *
 * Direction: 135deg (diagonal from top-left to bottom-right)
 */
export const IRIDESCENT_GRADIENT = {
  // React Native LinearGradient colors array
  colors: [
    BRAND_COLORS.gradientPink,
    BRAND_COLORS.gradientLavender,
    BRAND_COLORS.gradientMint,
    BRAND_COLORS.gradientTeal,
  ],

  // LinearGradient start/end positions
  start: { x: 0, y: 0 },
  end: { x: 1, y: 1 },

  // CSS linear-gradient string (web fallback)
  css: `linear-gradient(135deg, ${BRAND_COLORS.gradientPink} 0%, ${BRAND_COLORS.gradientLavender} 33%, ${BRAND_COLORS.gradientMint} 66%, ${BRAND_COLORS.gradientTeal} 100%)`,

  // CSS border-image string (for borders)
  borderImage: `linear-gradient(135deg, ${BRAND_COLORS.gradientPink}, ${BRAND_COLORS.gradientLavender}, ${BRAND_COLORS.gradientMint}, ${BRAND_COLORS.gradientTeal}) 1`,
} as const;

/**
 * Glassmorphism Tokens
 *
 * IMPORTANT: Requires fallback for web (backdrop-filter not universally supported)
 * - Modern browsers: backdrop-filter: blur(12px)
 * - Fallback: Semi-transparent frosted glass (no blur)
 */
export const GLASSMORPHISM = {
  // Blur intensity (NEVER animate this value)
  blurRadius: BRAND_COLORS.glassBlur,

  // Background colors (RGBA for transparency)
  backgroundLight: BRAND_COLORS.glassBackground, // White 90% opacity
  backgroundDark: 'rgba(0, 0, 0, 0.6)', // Black 60% opacity

  // Border
  border: BRAND_COLORS.glassBorder,

  // Shadow (elevation effect) - Native only
  // WARNING: Do NOT spread this on web - use shadowCSS instead
  shadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 10, // Android
  },

  // Shadow (elevation effect) - Web only (CSS box-shadow)
  shadowCSS: {
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.3)',
  },

  // CSS backdrop-filter string (web)
  backdropFilterCSS: 'blur(12px)',

  // Fallback CSS (Firefox, older Android)
  fallbackCSS: {
    backgroundColor: BRAND_COLORS.glassBackground,
    borderColor: BRAND_COLORS.glassBorder,
  },
} as const;

/**
 * Light Skeuomorphism - Neumorphic Shadows
 *
 * Applied to interactive elements ONLY (buttons, inputs, cards)
 * Max 50 elements with neumorphic effects (performance budget)
 */
export const NEUMORPHIC_SHADOW = {
  // Soft shadow (convex - raised)
  soft: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },

  // Medium shadow (interactive elements)
  medium: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },

  // Strong shadow (CTAs, modals)
  strong: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },

  // Gradient glow (iridescent CTA accent)
  gradientGlow: {
    shadowColor: BRAND_COLORS.gradientTeal,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 8,
    elevation: 0, // No elevation, just glow
  },
} as const;

/**
 * Typography Tokens
 *
 * Layer 1 (Free): Inter for all text
 * Layer 2 (Drama+): Neue Haas Grotesk Display headlines
 * Layer 3 (DramaX): Miller Display headlines (editorial luxury)
 */
export const TYPOGRAPHY = {
  // Font families
  fontDisplay: 'Inter', // Headlines (Layer 1)
  fontBody: 'Inter', // Body text (all tiers)
  fontNarrator: 'Courier New', // Narrator voice (monospace)

  // Font weights
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',

  // Font sizes (rem → px)
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
} as const;

/**
 * Animation Tokens
 *
 * CRITICAL: Respect prefers-reduced-motion
 * NEVER animate blur radius (GPU performance)
 */
export const ANIMATION = {
  // Duration
  fast: 180, // Routine interactions
  medium: 300, // Modal transitions
  slow: 600, // Celebration effects

  // Easing curves
  easeOut: [0.25, 0.1, 0.25, 1], // Cubic bezier
  easeInOut: [0.42, 0, 0.58, 1],
  spring: {
    tension: 65,
    friction: 11,
  },

  // Reduced motion fallback
  reducedMotion: {
    duration: 0, // Instant transitions
    enabled: false, // Disable animations
  },
} as const;

/**
 * Accessibility Tokens
 *
 * WCAG AA minimum (4.5:1), WCAG AAA preferred (7:1+)
 */
export const ACCESSIBILITY = {
  // Minimum tap target size (iOS/Android HIG)
  minTapTarget: 44,

  // Recommended tap target (56px for primary CTAs)
  recommendedTapTarget: 56,

  // Contrast ratios
  contrastAA: 4.5, // WCAG AA
  contrastAAA: 7.0, // WCAG AAA

  // Focus ring (keyboard navigation)
  focusRing: {
    width: 2,
    color: BRAND_COLORS.primary,
    offset: 2,
  },
} as const;

/**
 * Helper Functions
 */

/**
 * Convert hex color to rgba with opacity
 *
 * @param hex Hex color (e.g., '#000000' or '#000')
 * @param opacity Opacity value (0-1)
 * @returns rgba string (e.g., 'rgba(0, 0, 0, 0.25)')
 */
export function hexToRgba(hex: string, opacity: number): string {
  const cleanHex = hex.replace('#', '');
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(c => c + c).join('')
    : cleanHex;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Platform-aware box shadow creator
 *
 * Creates shadow styles that work correctly on both web and native platforms.
 * - Web: Returns CSS boxShadow property (modern, no deprecation warnings)
 * - Native: Returns React Native shadow* properties (iOS) + elevation (Android)
 *
 * @param options Shadow configuration
 * @returns Platform-specific style object
 *
 * @example
 * const cardStyle = {
 *   ...createBoxShadow({ color: '#000', offsetX: 0, offsetY: 4, blurRadius: 12, opacity: 0.15 }),
 * };
 */
export function createBoxShadow(options: {
  color: string;
  offsetX: number;
  offsetY: number;
  blurRadius: number;
  opacity: number;
  spreadRadius?: number;
  elevation?: number;
}): Record<string, unknown> {
  const { color, offsetX, offsetY, blurRadius, opacity, spreadRadius = 0, elevation } = options;

  // Lazy import to avoid circular dependencies and SSR issues
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { Platform } = require('react-native');

  if (Platform.OS === 'web') {
    const rgba = hexToRgba(color, opacity);
    return {
      boxShadow: `${offsetX}px ${offsetY}px ${blurRadius}px ${spreadRadius}px ${rgba}`,
    };
  }

  // Native (iOS/Android)
  return {
    shadowColor: color,
    shadowOffset: { width: offsetX, height: offsetY },
    shadowOpacity: opacity,
    shadowRadius: blurRadius,
    elevation: elevation ?? Math.round(blurRadius / 2),
  };
}

/**
 * Platform-aware text shadow creator
 *
 * Creates text shadow styles that work correctly on both web and native platforms.
 * - Web: Returns CSS textShadow property (modern, no deprecation warnings)
 * - Native: Returns React Native textShadow* properties
 *
 * @param options Text shadow configuration
 * @returns Platform-specific style object
 *
 * @example
 * const glowText = {
 *   ...createTextShadow({ color: '#D4A5FF', offsetX: 0, offsetY: 0, blurRadius: 8 }),
 * };
 */
export function createTextShadow(options: {
  color: string;
  offsetX: number;
  offsetY: number;
  blurRadius: number;
}): Record<string, unknown> {
  const { color, offsetX, offsetY, blurRadius } = options;

  // Lazy import to avoid circular dependencies and SSR issues
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { Platform } = require('react-native');

  if (Platform.OS === 'web') {
    return {
      textShadow: `${offsetX}px ${offsetY}px ${blurRadius}px ${color}`,
    };
  }

  // Native (iOS/Android)
  return {
    textShadowColor: color,
    textShadowOffset: { width: offsetX, height: offsetY },
    textShadowRadius: blurRadius,
  };
}

/**
 * Preset shadow configurations for common use cases
 * Use with createBoxShadow() for platform-aware styles
 */
export const SHADOW_PRESETS = {
  /** Soft elevation for cards and inputs */
  soft: { color: '#000', offsetX: 0, offsetY: 2, blurRadius: 4, opacity: 0.1, elevation: 2 },
  /** Medium elevation for interactive elements */
  medium: { color: '#000', offsetX: 0, offsetY: 4, blurRadius: 8, opacity: 0.15, elevation: 4 },
  /** Strong elevation for modals and CTAs */
  strong: { color: '#000', offsetX: 0, offsetY: 8, blurRadius: 16, opacity: 0.2, elevation: 8 },
  /** Glass card elevation */
  glass: { color: '#000', offsetX: 0, offsetY: 8, blurRadius: 16, opacity: 0.3, elevation: 10 },
  /** Glow effect (no offset, colored shadow) */
  glow: (glowColor: string) => ({
    color: glowColor,
    offsetX: 0,
    offsetY: 0,
    blurRadius: 8,
    opacity: 0.5,
    elevation: 0,
  }),
} as const;

/**
 * Get platform-specific glassmorphism styles
 *
 * @param variant 'light' | 'dark'
 * @returns Platform-specific style object
 */
export function getGlassmorphismStyle(variant: 'light' | 'dark' = 'light') {
  const backgroundColor =
    variant === 'light'
      ? GLASSMORPHISM.backgroundLight
      : GLASSMORPHISM.backgroundDark;

  return {
    backgroundColor,
    borderWidth: 1,
    borderColor: GLASSMORPHISM.border,
    ...GLASSMORPHISM.shadow,
  };
}

/**
 * Get accessible text color for background
 *
 * Uses WCAG 2.0 relative luminance formula with sRGB gamma correction
 * to ensure accurate contrast ratio calculations.
 *
 * @param backgroundColor Hex color (e.g., '#0F766E')
 * @returns 'white' | 'black' based on highest contrast ratio
 */
export function getAccessibleTextColor(backgroundColor: string): 'white' | 'black' {
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // WCAG 2.0 relative luminance with sRGB gamma correction
  const getChannelLuminance = (channel: number) => {
    const sRGB = channel / 255;
    return sRGB <= 0.03928
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  };

  const luminance =
    0.2126 * getChannelLuminance(r) +
    0.7152 * getChannelLuminance(g) +
    0.0722 * getChannelLuminance(b);

  // Calculate contrast ratios for black and white text
  // Contrast ratio formula: (L1 + 0.05) / (L2 + 0.05)
  // where L1 is lighter color, L2 is darker color
  const contrastWithBlack = (luminance + 0.05) / (0.0 + 0.05);
  const contrastWithWhite = (1.0 + 0.05) / (luminance + 0.05);

  // Return the color with the highest contrast ratio
  return contrastWithWhite > contrastWithBlack ? 'white' : 'black';
}
